import {
  Flex,
  Heading,
  List,
  ListIcon,
  ListItem,
  type ListProps,
  Text,
} from '@chakra-ui/layout';
import { groupBy } from 'lodash';

import { CircleIcon } from '../../../../../shared/ui/icons';
import { useReportBuilderConfig } from '../../../hooks/useReportBuilderConfig';
import { isHiddenColumn } from '../../02-entity-configuration/mw-reports/report-hidden-columns';
import { isHiddenCustomTaxonomySelectionKey } from '../../02-entity-configuration/mw-reports/useReportsRoleCustomTaxonomy';
import {
  EntityType,
  type ReportEntityConfigColumn,
} from '../../02-entity-configuration/mw-reports/utils';
import type { ReportBuilderDeliverable } from '../../report-builder.model';
import styles from './report-summary.module.css';

interface ReportSummaryProps {
  report: ReportBuilderDeliverable | null | undefined;
}

export const ReportSummary = ({ report }: ReportSummaryProps) => {
  const reportEntityMap = useReportBuilderConfig().config;

  if (!report || !reportEntityMap) return <Text>No Report Found</Text>;

  const selectedReportConfig = reportEntityMap[report.reportType];
  const reportType = selectedReportConfig?.columns.find(
    (column) => column?.id === report.reportType
  )?.label;

  const entityGroups = groupBy(
    // @ts-expect-error noUncheckedIndexedAccess
    reportEntityMap[report.reportType].columns
      // Exclude hidden custom taxonomy entities from UI
      .filter((column) => !isHiddenColumn(column)),
    (e) => e.reportEntityOptions.entityType
  );

  const entities = [
    {
      label: 'Focus Entities',
      selections: entityGroups[EntityType.MainEntity],
    },
    {
      label: 'Comparison Entities',
      selections: entityGroups[EntityType.ComparisonEntity],
    },
    { label: 'Filters', selections: entityGroups[EntityType.FilterEntity] },
  ].filter((entity) => entity.selections);

  return (
    <>
      <Flex>
        <Heading
          fontWeight={600}
          fontSize="16px"
          color="text.primary"
          mb={8}
          className={styles.heading}
        >
          {reportType} Report
        </Heading>
      </Flex>
      {entities.map((entity) => (
        <EntityList
          // @ts-expect-error noUncheckedIndexedAccess
          entity={entity}
          key={entity.label}
          report={report}
          mb={4}
          ml={10}
        />
      ))}
    </>
  );
};

interface EntityListProps extends ListProps {
  entity: {
    label: string;
    selections: ReportEntityConfigColumn[];
  };
  report: ReportBuilderDeliverable;
}

export const EntityList = ({
  entity,
  report,
  ...restProps
}: EntityListProps) => {
  return (
    <List spacing={2} {...restProps}>
      <Text className={styles.groupLabel}>{entity.label}</Text>
      {entity.selections.map((entity) => (
        <ListItem key={entity.id} pl={10}>
          <Flex alignItems="center">
            <ListIcon as={CircleIcon} color="green.500" boxSize={2} mt="5px" />
            <Text fontWeight={600} className={styles.entityText}>
              Type: {entity.label}
            </Text>
          </Flex>
          <List>
            {Object.keys(report.selections?.[entity.id] ?? {})
              // Filter out any pre-selected entities that we are intentionally hiding
              .filter(
                (selectionKey) =>
                  !isHiddenCustomTaxonomySelectionKey(selectionKey)
              )
              .map((selectionKey, i) => {
                const selectionItem =
                  report?.selections?.[entity.id]?.[selectionKey];
                return (
                  <ListItem key={i} pl={10}>
                    <Flex alignItems="center">
                      <ListIcon
                        as={CircleIcon}
                        color="green.500"
                        boxSize={2}
                        mt="5px"
                      />
                      <Text className={styles.entityText}>
                        {selectionItem?.item?.label ??
                          selectionItem?.item?.shortName ??
                          selectionItem?.item?.longName ??
                          selectionItem?.item?.id}
                      </Text>
                    </Flex>
                  </ListItem>
                );
              })}
          </List>
        </ListItem>
      ))}
    </List>
  );
};
